// src/api/api.js

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const SESSION_MS = 12 * 60 * 60 * 1000; // 12 hours

// ─── JWT decode (без бібліотеки) ──────────────────────────────────────────────
function decodeJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isJwtExpired(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return payload.exp * 1000 < Date.now();
}

// ─── 1) getStoredUser ─────────────────────────────────────────────────────────
export function getStoredUser() {
  const raw = localStorage.getItem("user") || sessionStorage.getItem("user");
  if (!raw) return null;

  try {
    const u = JSON.parse(raw);
    if (!u || typeof u !== "object") return null;

    // Перевіряємо 12-годинний таймер
    const loginAt = Number(
      localStorage.getItem("agro:loginAt") ||
      sessionStorage.getItem("agro:loginAt") ||
      0
    );
    if (!loginAt || Date.now() - loginAt > SESSION_MS) {
      _clearSession();
      return null;
    }

    // Перевіряємо exp у самому JWT токені
    const token = getToken(u);
    if (token && isJwtExpired(token)) {
      _clearSession();
      return null;
    }

    return u;
  } catch {
    _clearSession();
    return null;
  }
}

function _clearSession() {
  localStorage.removeItem("user");
  sessionStorage.removeItem("user");
  localStorage.removeItem("agro:loginAt");
  sessionStorage.removeItem("agro:loginAt");
}

// ─── 2) getToken ─────────────────────────────────────────────────────────────
export function getToken(user) {
  if (!user) return null;
  return (
    user.token ||
    user.accessToken ||
    user.jwt ||
    user.authToken ||
    (user.data && user.data.token) ||
    null
  );
}

// ─── 3) safeReadError — тільки читає тіло, БЕЗ redirect ──────────────────────
export async function safeReadError(res) {
  if (res?.status === 401) {
    // Читаємо повідомлення від сервера
    try {
      const ct = String(res.headers?.get?.("content-type") || "").toLowerCase();
      if (ct.includes("application/json")) {
        const j = await res.json().catch(() => null);
        if (j?.message) return j.message;
        if (j?.error)   return j.error;
      }
    } catch {}
    return "Помилка авторизації.";
  }

  try {
    const ct = String(
      (res && res.headers && res.headers.get && res.headers.get("content-type")) || ""
    ).toLowerCase();

    if (ct.includes("application/json")) {
      const j = await res.json().catch(() => null);
      if (!j) return `HTTP ${res.status}`;
      return j.message || j.error || j.detail || j.title || JSON.stringify(j);
    }

    const t = await res.text().catch(() => "");
    return t || `HTTP ${res.status}`;
  } catch {
    return null;
  }
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function isNonJsonBody(body) {
  if (body == null) return false;
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;
  if (typeof Blob !== "undefined" && body instanceof Blob) return true;
  if (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) return true;
  if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView && ArrayBuffer.isView(body)) return true;
  if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) return true;
  return false;
}

function isPlainObject(v) {
  if (v == null || typeof v !== "object") return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

function normalizeUrl(url) {
  const u = String(url || "").trim();
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (!API_BASE_URL) return u;
  return u.startsWith("/") ? `${API_BASE_URL}${u}` : `${API_BASE_URL}/${u}`;
}

function makeErrorResponse(status, message) {
  const safeStatus = status >= 200 && status <= 599 ? status : 503;
  return new Response(JSON.stringify({ message }), {
    status: safeStatus,
    headers: { "Content-Type": "application/json" },
  });
}

// ─── 4) apiFetch ─────────────────────────────────────────────────────────────
export async function apiFetch(user, url, options = {}) {
  // Перевіряємо 12-годинний таймер
  const loginAt = Number(
    localStorage.getItem("agro:loginAt") ||
    sessionStorage.getItem("agro:loginAt") ||
    0
  );
  if (!loginAt || Date.now() - loginAt > SESSION_MS) {
    signOut();
    return makeErrorResponse(401, "Сесія закінчилась. Будь ласка, увійдіть знову.");
  }

  const token = getToken(user);

  // Токена немає (user ще null — рано робити запит) або він expired
  if (!token) {
    return makeErrorResponse(401, "No token");
  }
  if (isJwtExpired(token)) {
    signOut();
    return makeErrorResponse(401, "Сесія закінчилась. Будь ласка, увійдіть знову.");
  }

  const finalUrl = normalizeUrl(url);
  const headers = new Headers(options.headers || {});
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);

  const method = String(options.method || "GET").toUpperCase();
  let body = options.body;
  if (isPlainObject(body)) body = JSON.stringify(body);

  const nonJson = isNonJsonBody(body);
  const hasBody = body != null && method !== "GET" && method !== "HEAD";

  if (hasBody && !nonJson) {
    if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  } else if (nonJson) {
    headers.delete("Content-Type");
  }

  let res;
  try {
    res = await fetch(finalUrl, {
      ...options,
      method,
      headers,
      ...(hasBody ? { body } : {}),
    });
  } catch {
    return makeErrorResponse(503, "Сервер недоступний");
  }

  return res;
}

// ─── signOut ─────────────────────────────────────────────────────────────────
export function signOut() {
  _clearSession();
  window.dispatchEvent(new CustomEvent("agro:session-expired"));
}

// ─── apiFetchJson ─────────────────────────────────────────────────────────────
export async function apiFetchJson(user, url, options = {}) {
  const res = await apiFetch(user, url, options);

  if (!res || !("ok" in res)) {
    return { ok: false, status: 0, error: "Bad response object" };
  }

  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      error: (await safeReadError(res)) || `HTTP ${res.status}`,
    };
  }

  const data = await res.json().catch(() => null);
  return { ok: true, status: res.status, data };
}
